import { useEffect, useState } from 'react';
import { createCsClient } from '@zep-us/cs';
import { TextArea } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import '@/shared/consts/events.quiz-school-package';
import { trackEvent } from '@zep/logger/tracking';
import { showErrorToast, showSuccessToast } from '@/shared/components/SuccessToast';
import { Button, DialogBody, DialogFooter, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { SingleSelect } from '@/ui_v3/components/select';
import { parsePublicQuizQuote, type PublicQuizQuote } from '../../shared/utils/publicQuizQuote';
import { type IssuedQuizQuote, quizQuoteCrmClient, QUOTE_VIEW_PATH } from '../../shared/utils/quizQuoteApi';
const INQUIRY_TYPES = [{
  value: 'payment_method',
  label: 'billing:quoteInquiry.paymentMethod'
}, {
  value: 'quote',
  label: 'billing:quoteInquiry.quote'
}, {
  value: 'purchase',
  label: 'billing:quoteInquiry.purchase'
}, {
  value: 'other',
  label: 'billing:planManage.inquiry.typeOther'
}] as const;
export function QuoteInquiryDialog({
  quote,
  onClose
}: {
  quote: IssuedQuizQuote;
  onClose: () => void;
}) {
  const {
    t,
    i18n
  } = useTranslation(['billing', 'common']);
  const [inquiryType, setInquiryType] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canonical, setCanonical] = useState<PublicQuizQuote | null>(null);
  const [loadError, setLoadError] = useState(false);
  const receipt = quote.crmQuote;
  useEffect(() => {
    if (!receipt) return;
    let active = true;
    void quizQuoteCrmClient.get(`/api/crm/v1/quotes/public/${encodeURIComponent(receipt.token)}`).then(response => {
      const document = parsePublicQuizQuote((response as {
        document?: unknown;
      }).document);
      if (active) setCanonical(document);
    }).catch(() => {
      if (active) setLoadError(true);
    });
    return () => {
      active = false;
    };
  }, [receipt]);
  const submit = async () => {
    if (!receipt || !canonical || !inquiryType || !message.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const client = createCsClient({
        baseUrl: process.env.NEXT_PUBLIC_STAGE === 'LOC' ? '/crm' : process.env.NEXT_PUBLIC_CRM_URL ?? ''
      });
      await client.submitTicket({
        name: quote.recipient.contactName,
        email: quote.recipient.email,
        subject: `ZEP QUIZ 견적서 문의 (${inquiryType})`,
        message: message.trim(),
        service: 'quiz',
        organization: quote.recipient.organizationName,
        fields: {
          inquiryType,
          quoteId: receipt.id
        },
        submitted_context: {
          quoteId: receipt.id,
          quoteViewPath: `${QUOTE_VIEW_PATH}/${receipt.token}`,
          inquiryType
        },
        website: ''
      });
      trackEvent('quiz_school_quote_inquiry_submit', {
        source: 'quote_result',
        quote_id: receipt.id,
        inquiry_type: inquiryType,
        result: 'success',
        region_policy: 'kr'
      });
      showSuccessToast(t('billing:planManage.inquiry.submitSuccess'));
      onClose();
    } catch {
      trackEvent('quiz_school_quote_inquiry_submit', {
        source: 'quote_result',
        quote_id: receipt.id,
        inquiry_type: inquiryType,
        result: 'failure',
        region_policy: 'kr'
      });
      showErrorToast(t('billing:planManage.inquiry.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };
  const row = (label: string, value: string) => <div className="flex items-center gap-md" data-sentry-component="row" data-sentry-source-file="QuoteInquiryDialog.tsx">
      <span className="w-[100px] shrink-0 text-body-md-medium text-alternative">
        {label}
      </span>
      <strong className="text-body-md-semibold text-neutral">{value}</strong>
    </div>;
  return <ResponsiveDialog onClose={onClose} size="md" forceDrawerOnMobile className="max-mobile-lg:!w-full mobile-lg:!w-[720px]" data-sentry-element="ResponsiveDialog" data-sentry-component="QuoteInquiryDialog" data-sentry-source-file="QuoteInquiryDialog.tsx">
      <DialogHeader title={t('common:play.header.inquiry')} hasCloseButton onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="QuoteInquiryDialog.tsx" />
      <p className="px-lg pb-md text-body-md-regular text-default">
        {t('billing:planManage.inquiry.operatingHours')}
      </p>
      <div className="mx-lg h-px bg-alternative" />
      <DialogBody data-sentry-element="DialogBody" data-sentry-source-file="QuoteInquiryDialog.tsx">
        <div className="flex flex-col gap-lg px-lg pb-none pt-md">
          <div className="flex flex-col gap-xs rounded-md bg-neutral px-md py-sm">
            {canonical ? <>
                {row(t('billing:paymentComplete.selectedPlan'), canonical.product.plan === 'pro' ? 'Pro' : 'Basic')}
                {row(t('billing:planManage.inquiry.planInfo'), canonical.product.pricingType === 'group' ? t('billing:planManage.billing.organization') : t('billing:planManage.billing.individual'))}
                {row(t('billing:paymentComplete.numberOfUsers'), `${canonical.product.userCount}${i18n.resolvedLanguage?.startsWith('en') ? ' ' : ''}${t('billing:paymentComplete.personUnit')}`)}
                {row(t('billing:usagePeriod'), `${canonical.product.monthCount}${t('billing:paymentComplete.monthUnit')}`)}
                {row(t('billing:paymentComplete.totalAmount'), new Intl.NumberFormat('ko-KR', {
              style: 'currency',
              currency: canonical.pricing.currency
            }).format(canonical.pricing.finalAmount))}
              </> : <p className="text-body-md-medium text-alternative">
                {loadError ? t('billing:planManage.inquiry.submitError') : '…'}
              </p>}
          </div>
          <SingleSelect title={t('billing:planManage.inquiry.type')} titleRightSlot={<span className="text-body-lg-medium text-strong">
                {t('billing:quoteInquiry.required')}
              </span>} required value={inquiryType} placeholder={t('billing:planManage.inquiry.typePlaceholder')} mobilePresentation="dropdown" options={INQUIRY_TYPES.map(item => ({
          value: item.value,
          label: t(item.label)
        }))} onValueChange={setInquiryType} data-sentry-element="SingleSelect" data-sentry-source-file="QuoteInquiryDialog.tsx" />
          <TextArea.Root required value={message} data-sentry-element="unknown" data-sentry-source-file="QuoteInquiryDialog.tsx">
            <TextArea.Label rightSlot={<span className="text-body-lg-medium text-strong">
                  {t('billing:quoteInquiry.required')}
                </span>} data-sentry-element="unknown" data-sentry-source-file="QuoteInquiryDialog.tsx">
              {t('billing:planManage.inquiry.details')}
            </TextArea.Label>
            <TextArea.Input required value={message} maxLength={4000} placeholder={t('billing:planManage.inquiry.detailsPlaceholder')} wrapperClassName="h-[96px]" className="h-full min-h-0" onChange={event => setMessage(event.target.value)} data-sentry-element="unknown" data-sentry-source-file="QuoteInquiryDialog.tsx" />
          </TextArea.Root>
        </div>
      </DialogBody>
      <DialogFooter className="flex justify-end" data-sentry-element="DialogFooter" data-sentry-source-file="QuoteInquiryDialog.tsx">
        <Button variant="contained" intent="strong" size="md" disabled={!canonical || !inquiryType || !message.trim() || isSubmitting} onClick={() => void submit()} data-sentry-element="Button" data-sentry-source-file="QuoteInquiryDialog.tsx">
          {t('billing:planManage.inquiry.submit')}
        </Button>
      </DialogFooter>
    </ResponsiveDialog>;
}